import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Shield, Loader2, Phone, ArrowLeft, MessageCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = '/api';
// WebSocket uses same origin as the page — works on any domain (rentmitrahub.com, preview, etc.)
const WS_BASE = window.location.origin
  .replace('https://', 'wss://')
  .replace('http://', 'ws://');

const CONTACT_RE = /\b\d{10}\b|\+91\s*\d{10}|\b\d{5}\s*\d{5}\b|[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/;

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function formatDate(iso) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function ChatWindow({ conversation, onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [contactWarning, setContactWarning] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const lastMsgIdRef = useRef(null);

  const conv = conversation;
  const convId = conv?.conversation_id;
  const isOwner = user?.user_id === conv?.owner_id;
  const otherName = isOwner ? conv?.tenant_name : conv?.owner_name;

  // ── fetch messages ────────────────────────────────────────────────────────
  const fetchMessages = useCallback(async (silent = false) => {
    if (!convId) return;
    try {
      const res = await fetch(`${API}/chat/conversations/${convId}/messages`, { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      const msgs = data.messages || [];
      const lastId = msgs[msgs.length - 1]?.message_id;
      if (lastId !== lastMsgIdRef.current) {
        lastMsgIdRef.current = lastId;
        setMessages(msgs);
      }
    } catch (_err) { console.error('fetchMessages failed:', _err); }
    if (!silent) setLoading(false);
  }, [convId]);

  // ── initial load + WebSocket + polling fallback ───────────────────────────
  useEffect(() => {
    if (!convId) return;
    setLoading(true);
    setMessages([]);
    lastMsgIdRef.current = null;
    fetchMessages();

    // Attempt WebSocket (may not work through all proxies — falls back to polling)
    let wsOpened = false;
    const ws = new WebSocket(`${WS_BASE}/api/chat/ws/${convId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      wsOpened = true;
      setWsConnected(true);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'message') {
          setMessages(prev => {
            if (prev.find(m => m.message_id === data.message_id)) return prev;
            return [...prev, data];
          });
          lastMsgIdRef.current = data.message_id;
        }
      } catch (_) { console.error('WebSocket frame parse error (non-message frame, ignored):', _); }
    };
    ws.onerror = () => { if (!wsOpened) startPolling(); };
    ws.onclose = () => { setWsConnected(false); startPolling(); };

    return () => {
      ws.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // startPolling is defined below but stable — adding to deps would create a circular ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId, fetchMessages]);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => fetchMessages(true), 3000);
  }, [fetchMessages]);

  // ── auto-scroll ───────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── send message ──────────────────────────────────────────────────────────
  const handleSend = async (e) => {
    e.preventDefault();
    const content = input.trim();
    if (!content || sending) return;
    if (CONTACT_RE.test(content)) {
      setContactWarning(true);
      setTimeout(() => setContactWarning(false), 4000);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/chat/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send');
      }
      const msg = await res.json();
      setMessages(prev => prev.find(m => m.message_id === msg.message_id) ? prev : [...prev, msg]);
      lastMsgIdRef.current = msg.message_id;
      setInput('');
    } catch (err) {
      toast.error(err.message);
    }
    setSending(false);
  };

  // ── group messages by date ────────────────────────────────────────────────
  const grouped = messages.reduce((acc, msg) => {
    const label = formatDate(msg.created_at);
    if (!acc[label]) acc[label] = [];
    acc[label].push(msg);
    return acc;
  }, {});

  if (!conv) return null;

  return (
    <div className="flex flex-col h-full bg-white" data-testid="chat-window">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 bg-white sticky top-0 z-10">
        {onBack && (
          <button onClick={onBack} className="text-slate-500 hover:text-slate-800 p-1 rounded-lg hover:bg-slate-100 transition-colors lg:hidden" data-testid="chat-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div className="w-9 h-9 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {otherName?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm truncate">{otherName}</p>
          <p className="text-xs text-slate-500 truncate">{conv.property_title} · {conv.property_city}</p>
        </div>
        <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${wsConnected ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-slate-400'}`}></span>
          {wsConnected ? 'Live' : 'Syncing'}
        </div>
      </div>

      {/* Privacy banner */}
      <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-100 px-4 py-2">
        <Shield className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
        <span className="text-xs text-amber-700">Contact details are protected. Phone numbers and emails are blocked.</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 bg-slate-50" data-testid="messages-list">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-3">
              <MessageCircle className="w-7 h-7 text-blue-600" />
            </div>
            <p className="text-slate-700 font-semibold text-sm">Start the conversation</p>
            <p className="text-slate-400 text-xs mt-1">Ask about availability, amenities, or schedule a visit.</p>
          </div>
        ) : (
          Object.entries(grouped).map(([date, dayMsgs]) => (
            <div key={date}>
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-xs text-slate-400 font-medium bg-slate-50 px-2">{date}</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              {dayMsgs.map((msg) => {
                const isMine = msg.sender_id === user?.user_id;
                return (
                  <div key={msg.message_id} className={`flex mb-1.5 ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.message_id}`}>
                    <div className={`max-w-[72%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                      isMine
                        ? 'bg-blue-700 text-white rounded-br-md'
                        : 'bg-white text-slate-800 border border-slate-100 shadow-sm rounded-bl-md'
                    }`}>
                      <p className="break-words">{msg.content}</p>
                      <p className={`text-[10px] mt-1 text-right ${isMine ? 'text-blue-200' : 'text-slate-400'}`}>{formatTime(msg.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Contact warning */}
      {contactWarning && (
        <div className="mx-4 mb-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 flex items-center gap-2">
          <Phone className="w-3.5 h-3.5 flex-shrink-0" />
          Contact details (phone/email) are not allowed. Please use the platform to communicate.
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-end gap-2 p-3 border-t border-slate-100 bg-white" data-testid="chat-input-form">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
          placeholder="Type a message…"
          rows={1}
          maxLength={1000}
          className="flex-1 resize-none px-4 py-2.5 border border-slate-200 rounded-2xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition-all bg-slate-50"
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          className="w-10 h-10 bg-blue-700 hover:bg-blue-800 disabled:opacity-40 text-white rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
